import React, { useCallback, useMemo } from 'react';
import { Map, get, getIn } from 'immutable';
import { StaticSelect } from '@kineticdata/react';
import { parseRenderOptions } from './utils';
import { FieldWrapper } from './FieldWrapper';
import classNames from 'classnames';
import {
  generateInput,
  generateSingleSelection,
  generateSingleSelectionsContainer,
  Status,
  SuggestionAction,
  SuggestionsContainer,
} from './TypeaheadDefaults';

export const MissingSecurityPolicyDefinitionMessage = ({ value, onNew }) => {
  return (
    <small className="form-text">
      The security policy definition named "<em>{value}</em>" is missing from
      the system.
      {typeof onNew === 'function' && (
        <>
          {' '}
          Please{' '}
          <button
            className="btn btn-text"
            type="button"
            onClick={() => onNew()}
          >
            recreate
          </button>{' '}
          it or select another definition.
        </>
      )}
    </small>
  );
};

export const ExistingValueMessage = ({ value, selectedOption, onEdit }) => {
  const type = get(selectedOption, 'type');
  return (
    <small className="form-text">
      Currently set to{' '}
      {typeof onEdit === 'function' ? (
        <button
          className="btn btn-text"
          type="button"
          onClick={() => onEdit(value)}
        >
          {value}
        </button>
      ) : (
        value
      )}
      {type ? ` of type ${type}.` : '.'}
    </small>
  );
};

const generateEmptySecurityPolicyValueMessage = (
  bindings,
  // The key of the binding from which policies can be inherited
  parentScope,
) => ({ label, onEdit }) => {
  const context = parentScope && get(bindings, parentScope);
  const kappSlug = getIn(bindings, ['kapp', 'slug']);
  const policyDefault =
    context &&
    context.has('securityPolicies') &&
    context
      .get('securityPolicies')
      .find(
        pol =>
          pol.get('endpoint') === label ||
          pol.get('endpoint') === `Default ${label}`,
        null,
        Map({}),
      )
      .get('name');

  return policyDefault ? (
    <small className="form-text">
      None selected (defaults to{' '}
      {typeof onEdit === 'function' ? (
        <button
          className="btn btn-text"
          type="button"
          onClick={() => onEdit(policyDefault)}
        >
          {policyDefault}
        </button>
      ) : (
        <>
          "<em>{policyDefault}</em>"
        </>
      )}{' '}
      by {kappSlug ? 'Kapp' : 'Space'}).
    </small>
  ) : (
    <small className="form-text">
      None selected (defaults to space admins).
    </small>
  );
};

const isMissingPolicy = props =>
  props.value &&
  !props.options.find(option => option.get('value') === props.value);

const selectionToValue = selection =>
  selection.get('label') || selection.get('value');

const Suggestion = ({ suggestion, active }) => (
  <div className={classNames('suggestion', { active })}>
    <div className="large">
      {suggestion.get('label') || suggestion.get('value')}
    </div>
    {suggestion.get('type') && (
      <div className="small">{suggestion.get('type')}</div>
    )}
  </div>
);

const getValueOption = props =>
  props.value
    ? props.options.find(option => option.get('value') === props.value) ||
      Map({
        label: props.value,
        value: props.value,
      })
    : null;

export const SecurityPolicyField = ({ onChange: onChangeOrig, ...props }) => {
  const renderOptions = parseRenderOptions(props);
  const isMissing = isMissingPolicy(props);
  const selectedOption = getValueOption(props);

  const components = useMemo(
    () => ({
      Input: generateInput({
        size: renderOptions.size,
        className: renderOptions.fieldClassName,
      }),
      Selection: generateSingleSelection(selectionToValue, {
        size: renderOptions.size,
      }),
      SelectionsContainer: generateSingleSelectionsContainer({
        size: renderOptions.size,
      }),
    }),
    [renderOptions.size, renderOptions.fieldClassName],
  );

  const EmptyValueMessage = useMemo(
    () =>
      generateEmptySecurityPolicyValueMessage(
        props.bindings,
        renderOptions.custom.parentScope,
      ),
    [props.bindings, renderOptions.custom.parentScope],
  );

  const onChange = useCallback(v => onChangeOrig(v ? v.get('value') : ''), [
    onChangeOrig,
  ]);

  const { onNew: onNewOrig, onEdit: onEditOrig } = renderOptions.custom;
  // Triggers the provided onNew function and after completion, sets the new
  // value into the field
  const onNew = useCallback(
    () => {
      if (typeof onNewOrig === 'function') {
        onNewOrig({
          type: renderOptions.custom.securityPolicyType,
          afterComplete: definition => {
            // After a new definition is added, call the onChangeOrig function
            // to set the value into the field
            if (definition) {
              onChangeOrig(definition?.name);
            }
          },
        });
      }
    },
    [onNewOrig, onChangeOrig, renderOptions.custom.securityPolicyType],
  );
  // Triggers the provided onEdit function and after completion, sets the
  // updated value into the field
  const onEdit = useCallback(
    name => {
      if (typeof onEditOrig === 'function') {
        onEditOrig({
          name,
          type: renderOptions.custom.securityPolicyType,
          afterComplete: definition => {
            // After the definition is updated, call the onChangeOrig function
            // to set the value into the field in case the name changed
            if (definition) {
              onChangeOrig(definition?.name);
            }
          },
        });
      }
    },
    [onEditOrig, onChangeOrig, renderOptions.custom.securityPolicyType],
  );
  // Triggers the provided onEdit function for the inherited policy, but
  // doesn't update the field value since it is not set in this case
  const onEditInherited = useCallback(
    name => {
      if (typeof onEditOrig === 'function') {
        onEditOrig({ name });
      }
    },
    [onEditOrig],
  );

  return (
    <FieldWrapper {...props} renderOptions={renderOptions}>
      <StaticSelect
        components={{
          ...components,
          Status,
          Suggestion,
          SuggestionAction,
          SuggestionsContainer,
        }}
        messages={{ empty: 'No security policies found.' }}
        disabled={!props.enabled}
        id={props.id}
        value={selectedOption}
        onChange={onChange}
        onBlur={props.onBlur}
        onFocus={props.onFocus}
        onNew={onNewOrig ? onNew : undefined}
        onNewLabel="Add New Security Definition"
        options={props.options}
        placeholder={renderOptions.placeholder}
        form={props.form}
        invalid={renderOptions.hasErrors || isMissing}
      />
      {!!props.value &&
        !isMissing && (
          <ExistingValueMessage
            {...props}
            selectedOption={selectedOption}
            onEdit={onEditOrig ? onEdit : undefined}
          />
        )}
      {!props.value && (
        <EmptyValueMessage
          {...props}
          onEdit={onEditOrig ? onEditInherited : undefined}
        />
      )}
      {isMissing && (
        <MissingSecurityPolicyDefinitionMessage
          {...props}
          onNew={onNewOrig ? onNew : undefined}
        />
      )}
    </FieldWrapper>
  );
};
